import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { redirectToPlatform } from '../api/auth';
import { redirectUserToPlatform } from './bizDataHelpers';
import { checkErrorTypeString } from './utils';
import UpToast from '../components/Common/UpToast';
import {
  redirectHostForApp,
  shouldSelectAtlasEnv,
} from './environment.js';
import SelectAtlasEnv from '../components/SelectAtlasEnv';

/**
 * Platform redirect shared by every view that calls /client/{app}/redirect.
 *
 * On production builds `redirect` calls the API straight away. On test
 * builds, a redirect that would land on Atlas staging/POS-int first shows
 * the SelectAtlasEnv picker (render `atlasEnvPicker` in the view) and only
 * proceeds with the chosen redirect_host.
 */
export default function usePlatformRedirect() {
  const [pendingRedirect, setPendingRedirect] = useState(null);

  const { isLoading, mutate: callRedirectPlatform } = useMutation({
    mutationFn: ({ appType, params }) => redirectToPlatform(appType, params),
    onSuccess: redirectUserToPlatform,
    onError: error => {
      const errorMessage = checkErrorTypeString(error?.response?.data?.message);
      UpToast.error(errorMessage);
    },
  });

  function redirect({ appType, params }) {
    // Drop a host that belongs to a different app (e.g. an atlas entry host
    // on a prime-only account) so the redirect falls back to the app default.
    const redirectHost = redirectHostForApp(appType, params?.redirect_host);
    const cleanParams = { ...params, redirect_host: redirectHost };

    if (shouldSelectAtlasEnv(appType, redirectHost)) {
      setPendingRedirect({ appType, params: cleanParams });
      return;
    }

    callRedirectPlatform({ appType, params: cleanParams });
  }

  function onSelectEnv(env) {
    if (!pendingRedirect) {
      return;
    }

    callRedirectPlatform({
      appType: pendingRedirect.appType,
      params: { ...pendingRedirect.params, redirect_host: env.host },
    });
    setPendingRedirect(null);
  }

  const atlasEnvPicker = pendingRedirect ? (
    <SelectAtlasEnv
      onSelect={onSelectEnv}
      onClose={() => setPendingRedirect(null)}
    />
  ) : null;

  return { isLoading, redirect, atlasEnvPicker };
}
